import React, { useCallback, useEffect, useState } from 'react';
import store from './journalStore';

/* 💌 The Letterbox — real letters between the two of us, kept in the vault.
   A letter can be sealed until a date: the server refuses to hand over the
   body before then (even devtools can't peek), and the envelope shows a
   countdown instead. The sender gets a little read receipt once it's opened. */

const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const prettyDate = (ds) => {
  const [y, m, dd] = ds.split('-').map(Number);
  return new Date(y, m - 1, dd).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
};
const prettyWhen = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  } catch {
    return '';
  }
};
const daysUntil = (ds) => {
  const [y, m, dd] = ds.split('-').map(Number);
  const now = new Date();
  const t0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.max(0, Math.round((new Date(y, m - 1, dd) - t0) / 86400000));
};

export default function Letterbox({ passcode, me }) {
  const other = me === 'Anusha' ? 'Tanush' : 'Anusha';
  const [letters, setLetters] = useState(null);
  const [reading, setReading] = useState(null); // letter object
  const [toast, setToast] = useState('');
  const [shakeId, setShakeId] = useState(null);

  const [composing, setComposing] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sealMode, setSealMode] = useState('now'); // now | date
  const [sealDate, setSealDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      setLetters(await store.letters(passcode, me));
    } catch (e) {
      setLetters([]);
      setErr('The letterbox is stuck — try again in a moment.');
    }
  }, [passcode, me]);

  useEffect(() => {
    load();
  }, [load]);

  const showToast = (m) => {
    setToast(m);
    setTimeout(() => setToast(''), 2000);
  };

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSaving(true);
    setErr('');
    try {
      await store.addLetter(passcode, {
        from: me,
        to: other,
        title: title.trim(),
        body: body.trim(),
        sealedUntil: sealMode === 'date' && sealDate ? sealDate : null,
      });
      setTitle('');
      setBody('');
      setSealMode('now');
      setSealDate('');
      setComposing(false);
      showToast(sealMode === 'date' && sealDate ? 'Sealed and posted 🔒💌' : 'Posted 💌');
      await load();
    } catch (e2) {
      setErr(e2.message || 'Could not send that letter.');
    } finally {
      setSaving(false);
    }
  };

  const openLetter = async (l) => {
    if (l.sealed) {
      setShakeId(l.id);
      setTimeout(() => setShakeId(null), 500);
      showToast('No peeking, baby 🔒');
      return;
    }
    setReading(l);
    if (l.to === me && !l.openedAt) {
      try {
        const opened = await store.openLetter(passcode, l.id);
        if (opened) setLetters((ls) => (ls || []).map((x) => (x.id === l.id ? { ...x, openedAt: opened.openedAt } : x)));
      } catch (e) {
        /* receipt is best-effort */
      }
    }
  };

  const delLetter = async (id) => {
    try {
      await store.delLetter(passcode, id);
      await load();
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => {
    if (!reading) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setReading(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [reading]);

  const inbox = (letters || []).filter((l) => l.to === me).sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  const outbox = (letters || []).filter((l) => l.from === me).sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));

  const minSeal = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return ymd(d);
  })();

  const row = (l, mine) => {
    const sealed = !!l.sealed;
    const unopened = !sealed && l.to === me && !l.openedAt;
    return (
      <button
        type="button"
        key={l.id}
        className={`aj-lb-row${sealed ? ' is-sealed' : ''}${unopened ? ' is-new' : ''}${shakeId === l.id ? ' is-shake' : ''}`}
        onClick={() => (mine ? setReading(l) : openLetter(l))}
      >
        <span className="aj-lb-ico" aria-hidden="true">{sealed ? '🔒' : unopened ? '💌' : '✉️'}</span>
        <span className="aj-lb-info">
          <span className="aj-lb-title">
            {l.title || (sealed ? 'a sealed letter' : 'a letter')}
            {unopened && <span className="aj-lb-newbadge">NEW</span>}
          </span>
          <span className="aj-lb-sub">
            {mine ? `to ${l.to}` : `from ${l.from}`} · {prettyWhen(l.createdAt)}
            {sealed && ` · unseals ${prettyDate(l.sealedUntil)} (${daysUntil(l.sealedUntil)} day${daysUntil(l.sealedUntil) === 1 ? '' : 's'})`}
            {mine && !sealed && (l.openedAt ? ` · opened ${prettyWhen(l.openedAt)} 🥰` : ' · not opened yet')}
          </span>
        </span>
        {mine && (
          <span
            className="aj-del"
            role="button"
            tabIndex={-1}
            aria-label="delete letter"
            onClick={(e) => {
              e.stopPropagation();
              delLetter(l.id);
            }}
          >
            ×
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="aj-lb">
      <div className="aj-days-head">
        <h2 className="aj-days-title">the letterbox</h2>
        <p className="aj-days-sub">real letters, kept forever. seal one until a day that matters. 🔒</p>
      </div>

      {!composing ? (
        <div className="aj-lb-composewrap">
          <button type="button" className="aj-btn" onClick={() => setComposing(true)}>
            ✍️ write to {other}
          </button>
        </div>
      ) : (
        <form className="aj-compose aj-lb-compose" onSubmit={send}>
          <input
            className="aj-input"
            value={title}
            maxLength={120}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="a tiny title (optional)"
          />
          <textarea
            className="aj-textarea"
            rows={6}
            value={body}
            autoFocus
            onChange={(e) => setBody(e.target.value)}
            placeholder={`Dear ${other}…`}
          />
          <div className="aj-ev-formrow">
            <div className="aj-who">
              <button
                type="button"
                className={`aj-chip${sealMode === 'now' ? ' is-on' : ''}`}
                onClick={() => setSealMode('now')}
              >
                open right away ✉️
              </button>
              <button
                type="button"
                className={`aj-chip${sealMode === 'date' ? ' is-on' : ''}`}
                onClick={() => setSealMode('date')}
              >
                seal until… 🔒
              </button>
              {sealMode === 'date' && (
                <input
                  type="date"
                  className="aj-date-input"
                  value={sealDate}
                  min={minSeal}
                  onChange={(e) => setSealDate(e.target.value)}
                  required
                />
              )}
            </div>
            <div className="aj-ev-formbtns">
              <button type="button" className="aj-btn-ghost aj-btn-ghost-sm" onClick={() => setComposing(false)}>
                cancel
              </button>
              <button type="submit" className="aj-btn aj-btn-sm" disabled={saving || !body.trim() || (sealMode === 'date' && !sealDate)}>
                {saving ? 'Posting…' : sealMode === 'date' ? 'Seal & post 🔒' : 'Post it 💌'}
              </button>
            </div>
          </div>
          {err && <div className="aj-err">{err}</div>}
        </form>
      )}

      {letters === null ? (
        <div className="aj-muted" style={{ textAlign: 'center' }}>opening the letterbox…</div>
      ) : (
        <>
          <div className="aj-lb-section">
            <div className="aj-lb-heading">for you 💌</div>
            {inbox.length === 0 ? (
              <div className="aj-muted">nothing yet — but the day is young. 😏</div>
            ) : (
              <div className="aj-lb-list">{inbox.map((l) => row(l, false))}</div>
            )}
          </div>

          <div className="aj-lb-section">
            <div className="aj-lb-heading">from you 📤</div>
            {outbox.length === 0 ? (
              <div className="aj-muted">you haven&rsquo;t written {other} a letter yet. fix that. 💕</div>
            ) : (
              <div className="aj-lb-list">{outbox.map((l) => row(l, true))}</div>
            )}
          </div>
        </>
      )}

      {reading && (
        <div className="aj-letter-overlay" onClick={() => setReading(null)}>
          <div className="aj-letter-paper" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="aj-letter-close" onClick={() => setReading(null)} aria-label="close letter">
              ×
            </button>
            <div className="aj-letter-head">
              <span className="aj-letter-seal" aria-hidden="true">💌</span>
              <h4 className="aj-letter-title">{reading.title || `a letter from ${reading.from}`}</h4>
            </div>
            <div className="aj-letter-body">
              <p className="aj-letter-meta-line">
                from {reading.from} · {prettyDate(reading.createdAt.slice(0, 10))}
                {reading.sealedUntil && ` · was sealed until ${prettyDate(reading.sealedUntil)}`}
              </p>
              <p className="aj-lb-bodytext">{reading.body}</p>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="aj-ss-toast">{toast}</div>}
    </div>
  );
}
